/**
 * ShopSalesDetail Component
 * --
 * 작성자 - Owen
 * 작성일 - 2025 07 16 수요일
 * 마지막 수정일 - 2025 07 16 수요일
 */
import React from 'react';
import { Col, Content, ModalLayout, Row } from 'components';
import dayjs from 'dayjs';
import { useGetShopOrders } from 'hooks/ShopOrderHooks';
import { useGetParcelServices } from 'hooks/ParcelServiceHooks';
import { useGetRentals } from 'hooks/RentalHooks';
import { Avatar, Flex, Spin, Table, Tag } from 'antd';
import { stringToMoneyFormat } from 'utils';
import { DELIVERY_STATUS, PRODUCT_TYPE } from 'constants/ProductConstants';
import DeliveryCompany from 'constants/DeliveryCompany';
import { PURCHASE_STATUS } from 'constants/OrderConstants';
import { PictureOutlined } from '@ant-design/icons';

const ShopSalesDetail = (props) => {
  /**
   * SECTION - Init
   */

  /**!SECTION - Init */

  /**
   * SECTION - Route
   */

  /**!SECTION - Route */

  /**
   * SECTION - Props
   */
  const {
    open = false,
    onClose = () => {},
    date = null,
    shopId = null,
    summary = null,
  } = props;
  /**!SECTION - Props */

  /**
   * SECTION - Route
   */

  /**!SECTION - Route */

  /**
   * SECTION - Variable
   */
  const PAYMENT_STATE = React.useMemo(() => {
    return {
      COMPLETED: {
        title: '결제완료',
        color: 'green',
      },
      PARTIALLY_REFUNDED: {
        title: '부분환불',
        color: 'orange',
      },
      REFUNDED: {
        title: '전체환불',
        color: 'red',
      },
    };
  }, []);

  const CYCLE = React.useMemo(() => {
    return {
      W: '주',
      M: '월',
      Y: '년',
    };
  }, []);
  /**!SECTION - Variable */

  /**
   * SECTION - Query Data
   */
  // Shop Order 데이터 조회
  const { data: ShopOrders } = useGetShopOrders(
    {
      shop_information_id: shopId,
      start_date: dayjs(date).startOf('day').unix(),
      end_date: dayjs(date).endOf('day').unix(),
      join_tables: '["user"]',
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
      enabled: shopId && date && open ? true : false,
    }
  );
  // Parcel 데이터 조회
  const { data: Parcels } = useGetParcelServices(
    {
      shop_information_id: shopId,
      start_date: dayjs(date).startOf('day').unix(),
      end_date: dayjs(date).endOf('day').unix(),
      get_type: 'PAYMENT',
      join_tables: '["refund"]',
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
      enabled: shopId && date && open ? true : false,
    }
  );
  // Rental 데이터 조회
  const { data: Rentals } = useGetRentals(
    {
      shop_information_id: shopId,
      start_date: dayjs(date).startOf('day').unix(),
      end_date: dayjs(date).endOf('day').unix(),
      get_type: 'PAYMENT',
      join_tables: '["refund"]',
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
      enabled: shopId && date && open ? true : false,
    }
  );
  /**!SECTION - Query Data */

  /**
   * SECTION - Mutate
   */

  /**!SECTION - Mutate */

  /**
   * SECTION - State
   */
  const [isLoading, setIsLoading] = React.useState(false);
  /**!SECTION - State */

  /**
   * SECTION - Function
   */
  // Parcel, Rental 상세내역 테이블 컬럼 정제
  const getChildrenColumns = React.useCallback(
    (t = '') => {
      let columns = [];

      switch (t) {
        case 'DELIVERY':
        case 'STORE_PICKUP': {
          columns = [
            {
              title: '품목별 주문ID',
              key: 'parcel_service_id',
              dataIndex: 'parcel_service_id',
              width: 150,
            },
            {
              title: '상품정보',
              key: 'product',
              dataIndex: 'product',
              width: 350,
              render: (item) => (
                <Flex gap={4} align="center">
                  <Avatar
                    shape="square"
                    src={item?.thumbnail}
                    size={64}
                    icon={<PictureOutlined />}
                  />
                  <Flex vertical>
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: '#BDBDBD',
                      }}
                    >
                      {item?.shop_information_name}
                    </p>
                    <p style={{ fontSize: 12, fontWeight: 700 }}>
                      {item?.product_name}
                    </p>

                    {item?.product_option && item?.product_option.length ? (
                      <p style={{ fontSize: 10 }}>
                        {item?.product_option
                          .map(
                            (item) =>
                              `${item.option_title} - ${
                                item.option[0].option_name
                              } ${`(${stringToMoneyFormat(
                                item.option[0].option_price
                              )})`}`
                          )
                          .join(' / ')}
                      </p>
                    ) : null}

                    <p style={{ fontSize: 10 }}>
                      {stringToMoneyFormat(
                        item?.price + item?.option_price,
                        '원'
                      )}
                    </p>
                  </Flex>
                </Flex>
              ),
            },
            {
              title: '구매수량',
              key: 'num',
              dataIndex: 'num',
              width: 130,
              render: (item) => stringToMoneyFormat(item),
            },
            {
              title: '결제금액',
              key: 'payment_price',
              dataIndex: 'payment_price',
              width: 130,
              render: (item) => stringToMoneyFormat(item),
            },
            {
              title: '할인',
              key: 'coupon_info',
              dataIndex: 'coupon_info',
              width: 300,
              render: (item, record) =>
                item ? (
                  <Flex vertical gap={2}>
                    <p style={{ fontWeight: 500 }}>
                      {item.policy_coupon.coupon_name}
                    </p>
                    <p style={{ fontSize: 12, color: '#FF4242' }}>
                      {stringToMoneyFormat(item.discount_price, '원 할인')}
                    </p>
                  </Flex>
                ) : (
                  '-'
                ),
            },
            {
              title: '사용포인트',
              key: 'use_point',
              dataIndex: 'product',
              width: 130,
              render: (item) => stringToMoneyFormat(item.use_point),
            },
            {
              title: '적립금',
              key: 'reserve_price',
              dataIndex: 'reserve_price',
              width: 130,
              render: (item) => stringToMoneyFormat(item),
            },
            {
              title: '배송/픽업 상태',
              key: 'delivery_status',
              dataIndex: 'delivery_status',
              width: 100,
              render: (item, record) => (
                <Flex vertical gap={4}>
                  <Tag color={DELIVERY_STATUS[item]?.color}>
                    {DELIVERY_STATUS[item]?.title}
                  </Tag>

                  {record.delivery_date ? (
                    <p style={{ fontSize: 12, color: '#666666' }}>
                      {`${
                        record.type === 'DELIVERY' ? '배송일: ' : '픽업일: '
                      }`}
                      {dayjs
                        .unix(record.delivery_date)
                        .format('YYYY.MM.DD HH:mm')}
                    </p>
                  ) : null}
                </Flex>
              ),
            },
            {
              title: '송장번호',
              key: 'invoice_number',
              dataIndex: 'invoice_number',
              width: 200,
              render: (item, record) => (
                <Flex gap={4}>
                  {item ? (
                    <React.Fragment>
                      <p>
                        {DeliveryCompany[record.delivery_company]?.label ??
                          '한진택배'}
                      </p>
                      <p>({item ?? '1234567890112'})</p>
                    </React.Fragment>
                  ) : (
                    '-'
                  )}
                </Flex>
              ),
            },
            {
              title: '구매상태',
              key: 'purchase_status',
              dataIndex: 'purchase_status',
              width: 100,
              render: (item) => (
                <Tag color={PURCHASE_STATUS[item]?.color}>
                  {PURCHASE_STATUS[item]?.title}
                </Tag>
              ),
            },
          ];
          break;
        }
        case 'SUBSCRIPTION': {
          columns = columns = [
            {
              title: '품목별 주문ID',
              key: 'parcel_service_id',
              dataIndex: 'parcel_service_id',
              width: 150,
            },
            {
              title: '상품정보',
              key: 'product',
              dataIndex: 'product',
              width: 350,
              render: (item) => (
                <Flex gap={4} align="center">
                  <Avatar
                    shape="square"
                    src={item?.thumbnail}
                    size={64}
                    icon={<PictureOutlined />}
                  />
                  <Flex vertical>
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: '#BDBDBD',
                      }}
                    >
                      {item?.shop_information_name}
                    </p>
                    <p style={{ fontSize: 12, fontWeight: 700 }}>
                      {item?.product_name}
                    </p>

                    {item?.product_option && item?.product_option.length ? (
                      <p style={{ fontSize: 10 }}>
                        {item?.product_option
                          .map(
                            (item) =>
                              `${item.option_title} - ${
                                item.option[0].option_name
                              } ${`(${stringToMoneyFormat(
                                item.option[0].option_price
                              )})`}`
                          )
                          .join(' / ')}
                      </p>
                    ) : null}

                    <p style={{ fontSize: 10 }}>
                      {stringToMoneyFormat(
                        item?.price + item?.option_price,
                        '원'
                      )}
                    </p>
                  </Flex>
                </Flex>
              ),
            },
            {
              title: '구매수량',
              key: 'num',
              dataIndex: 'regular_payment_info',
              width: 130,
              render: (item) => stringToMoneyFormat(item.num),
            },
            {
              title: '구매주기',
              key: 'regular_cycle',
              dataIndex: 'regular_cycle',
              width: 130,
              render: (item) => {
                const split = item.split('-');

                return `${split[0]}${CYCLE[split[1]]}`;
              },
            },
            {
              title: '배송횟수',
              key: 'max_regular_delivery_count',
              dataIndex: 'max_regular_delivery_count',
              width: 130,
              render: (item) => (item ? `${item} 회` : '제한없음'),
            },
            {
              title: '구매회차',
              key: 'regular_delivery_count',
              dataIndex: 'regular_payment_info',
              width: 130,
              render: (item) =>
                stringToMoneyFormat(item.regular_payment_id, '회차'),
            },
            {
              title: '다음 결제 예정일',
              key: 'regular_updated_at',
              dataIndex: 'regular_updated_at',
              width: 130,
              render: (item) =>
                item ? dayjs.unix(item).format('YYYY.MM.DD') : '-',
            },
            {
              title: '결제금액',
              key: 'payment_price',
              dataIndex: 'regular_payment_info',
              width: 130,
              render: (item) => stringToMoneyFormat(item.price),
            },
            {
              title: '할인',
              key: 'coupon_info',
              dataIndex: 'coupon_info',
              width: 300,
              render: (item, record) =>
                item && record.regular_payment_info.regular_payment_id === 1 ? (
                  <Flex vertical gap={2}>
                    <p style={{ fontWeight: 500 }}>
                      {item.policy_coupon.coupon_name}
                    </p>
                    <p style={{ fontSize: 12, color: '#FF4242' }}>
                      {stringToMoneyFormat(item.discount_price, '원 할인')}
                    </p>
                  </Flex>
                ) : (
                  '-'
                ),
            },
            {
              title: '사용포인트',
              key: 'use_point',
              dataIndex: 'product',
              width: 130,
              render: (item, record) =>
                item.use_point &&
                record.regular_payment_info.regular_payment_id === 1
                  ? stringToMoneyFormat(item.use_point)
                  : 0,
            },
            {
              title: '적립금',
              key: 'reserve_price',
              dataIndex: 'regular_payment_info',
              width: 130,
              render: (item) => stringToMoneyFormat(item.reserve_price),
            },
            {
              title: '배송상태',
              key: 'delivery_status',
              dataIndex: 'regular_payment_info',
              width: 100,
              render: (item) => (
                <Flex vertical gap={4}>
                  <Tag color={DELIVERY_STATUS[item?.delivery_status]?.color}>
                    {DELIVERY_STATUS[item?.delivery_status]?.title}
                  </Tag>

                  {item.delivery_date ? (
                    <p style={{ fontSize: 12, color: '#666666' }}>
                      배송일{': '}
                      {dayjs
                        .unix(item.delivery_date)
                        .format('YYYY.MM.DD HH:mm')}
                    </p>
                  ) : null}
                </Flex>
              ),
            },
            {
              title: '송장번호',
              key: 'invoice_number',
              dataIndex: 'regular_payment_info',
              width: 200,
              render: (item, record) => (
                <Flex gap={4}>
                  {item.invoice_number ? (
                    <React.Fragment>
                      <p>{DeliveryCompany[item.delivery_company]?.label}</p>
                      <p>({item.invoice_number})</p>
                    </React.Fragment>
                  ) : (
                    '-'
                  )}
                </Flex>
              ),
            },
            {
              title: '구매상태',
              key: 'purchase_status',
              dataIndex: 'regular_payment_info',
              width: 100,
              render: (item) => (
                <Tag color={PURCHASE_STATUS[item?.purchase_status]?.color}>
                  {PURCHASE_STATUS[item?.purchase_status]?.title}
                </Tag>
              ),
            },
          ];
          break;
        }
        case 'RENTAL': {
          columns = columns = [
            {
              title: '품목별 주문ID',
              key: 'rental_id',
              dataIndex: 'rental_id',
              width: 150,
            },
            {
              title: '상품정보',
              key: 'product',
              dataIndex: 'product',
              width: 350,
              render: (item) => (
                <Flex gap={4} align="center">
                  <Avatar
                    shape="square"
                    src={item?.thumbnail}
                    size={64}
                    icon={<PictureOutlined />}
                  />
                  <Flex vertical>
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: '#BDBDBD',
                      }}
                    >
                      {item?.shop_information_name}
                    </p>
                    <p style={{ fontSize: 12, fontWeight: 700 }}>
                      {item?.product_name}
                    </p>

                    {item?.product_option && item?.product_option.length ? (
                      <p style={{ fontSize: 10 }}>
                        {item?.product_option
                          .map(
                            (item) =>
                              `${item.option_title} - ${
                                item.option[0].option_name
                              } ${`(${stringToMoneyFormat(
                                item.option[0].option_price
                              )})`}`
                          )
                          .join(' / ')}
                      </p>
                    ) : null}

                    <p style={{ fontSize: 10 }}>
                      {stringToMoneyFormat(
                        item?.price + item?.option_price,
                        '원'
                      )}
                    </p>
                  </Flex>
                </Flex>
              ),
            },
            {
              title: '렌탈기간',
              key: 'rental_date',
              dataIndex: 'rental_start_date',
              width: 150,
              render: (item, record) =>
                item ? (
                  <Flex>
                    <p>{dayjs.unix(item).format('YYYY.MM.DD')}</p>
                    <p>
                      ~{' '}
                      {dayjs.unix(record.rental_end_date).format('YYYY.MM.DD')}
                    </p>
                  </Flex>
                ) : (
                  '-'
                ),
            },
            {
              title: '계약기간',
              key: 'contract_date',
              dataIndex: 'contract_date',
              width: 130,
            },
            {
              title: '의무사용기간',
              key: 'mandatory_usage_date',
              dataIndex: 'mandatory_usage_date',
              width: 130,
            },
            {
              title: '선납입금',
              key: 'advance_price',
              dataIndex: 'advance_price',
              width: 130,
              render: (item) => stringToMoneyFormat(item),
            },
            {
              title: '추가비용',
              key: 'additional_cost',
              dataIndex: 'additional_cost',
              width: 130,
              render: (item) => stringToMoneyFormat(item),
            },
            {
              title: '관리유형',
              key: 'management',
              dataIndex: 'rental_option',
              width: 130,
              render: (item) => item?.management?.option_name,
            },
            {
              title: '결제회차',
              key: 'rental_delivery_count',
              dataIndex: 'rental_payment_info',
              width: 130,
              render: (item) =>
                stringToMoneyFormat(item.rental_payment_id, '회차'),
            },
            {
              title: '다음 결제 예정일',
              key: 'rental_updated_at',
              dataIndex: 'rental_updated_at',
              width: 130,
              render: (item) =>
                item ? dayjs.unix(item).format('YYYY.MM.DD') : '-',
            },
            {
              title: '결제금액',
              key: 'payment_price',
              dataIndex: 'rental_payment_info',
              width: 130,
              render: (item) => stringToMoneyFormat(item.price),
            },
            {
              title: '사용포인트',
              key: 'use_point',
              dataIndex: 'product',
              width: 130,
              render: (item, record) =>
                item.use_point &&
                record.regular_payment_info.regular_payment_id === 1
                  ? stringToMoneyFormat(item.use_point)
                  : 0,
            },
            {
              title: '적립금',
              key: 'reserve_price',
              dataIndex: 'rental_payment_info',
              width: 130,
              render: (item) => stringToMoneyFormat(item.reserve_price),
            },
            {
              title: '배송상태',
              key: 'delivery_status',
              dataIndex: 'delivery_status',
              width: 100,
              render: (item, record) => (
                <Flex vertical gap={4}>
                  <Tag color={DELIVERY_STATUS[item]?.color}>
                    {DELIVERY_STATUS[item]?.title}
                  </Tag>

                  {record.delivery_date ? (
                    <p style={{ fontSize: 12, color: '#666666' }}>
                      배송일{': '}
                      {dayjs
                        .unix(record.delivery_date)
                        .format('YYYY.MM.DD HH:mm')}
                    </p>
                  ) : null}
                </Flex>
              ),
            },
            {
              title: '송장번호',
              key: 'invoice_number',
              dataIndex: 'invoice_number',
              width: 200,
              render: (item, record) => (
                <Flex gap={4}>
                  {item ? (
                    <React.Fragment>
                      <p>{DeliveryCompany[record.delivery_company]?.label}</p>
                      <p>({item})</p>
                    </React.Fragment>
                  ) : (
                    '-'
                  )}
                </Flex>
              ),
            },
            {
              title: '구매상태',
              key: 'purchase_status',
              dataIndex: 'rental_payment_info',
              width: 100,
              render: (item) => (
                <Tag color={PURCHASE_STATUS[item?.purchase_status]?.color}>
                  {PURCHASE_STATUS[item?.purchase_status]?.title}
                </Tag>
              ),
            },
          ];
          break;
        }
        default:
          break;
      }

      return columns;
    },
    [CYCLE]
  );

  // Parcel, Rental 상세내역 테이블 데이터 정제
  const getChildrenData = React.useCallback((rows) => {
    const { type, rental, parcel_service } = rows;

    let data = [];

    switch (type) {
      case 'DELIVERY':
      case 'STORE_PICKUP':
      case 'SUBSCRIPTION': {
        data = parcel_service;
        break;
      }
      case 'RENTAL': {
        data = rental;
        break;
      }
      default:
        break;
    }

    return data;
  }, []);
  /**!SECTION - Function */

  /**
   * SECTION - Hooks
   */
  // 주문 데이터 정제
  const Orders = React.useMemo(() => {
    if (!ShopOrders.length) return [];

    setIsLoading(true);

    const newData = ShopOrders.map((item) => ({
      key: item.shop_order_id,
      ...item,
      parcel_service:
        Parcels.filter(
          (parcel) => item.shop_order_id === parcel.shop_order_id
        ) ?? [],
      rental:
        Rentals.filter(
          (rental) => item.shop_order_id === rental.shop_order_id
        ) ?? [],
    }));

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return newData;
  }, [ShopOrders, Parcels, Rentals]);

  // 합산 테이블 컬럼 설정
  const SummaryColumns = React.useMemo(() => {
    return [
      {
        title: '날짜',
        key: 'key',
        dataIndex: 'key',
        fixed: 'left',
        render: (item) => dayjs(item).locale('ko').format('YYYY-MM-DD (dd)'),
      },
      {
        title: (
          <span style={{ lineHeight: 0.5 }}>
            Total
            <span style={{ fontSize: '0.8em', color: '#7f7f7f' }}>
              (총매출+할인+환불)
            </span>
          </span>
        ),
        key: 'totalAmount',
        dataIndex: 'totalAmount',
        render: (item) => stringToMoneyFormat(item),
      },
      {
        title: (
          <span style={{ lineHeight: 0.5 }}>
            순매출
            <span style={{ fontSize: '0.8em', color: '#7f7f7f' }}>
              (총매출-할인)
            </span>
          </span>
        ),
        key: 'totalNetAmount',
        dataIndex: 'totalNetAmount',
        render: (item) => stringToMoneyFormat(item),
      },
      {
        title: '총 결제건수',
        key: 'totalCount',
        dataIndex: 'totalCount',
        render: (item) => stringToMoneyFormat(item),
      },
      {
        title: '총 할인금액',
        key: 'totalDiscount',
        dataIndex: 'totalDiscount',
        render: (item) => (
          <span style={{ color: '#FF4242' }}>-{stringToMoneyFormat(item)}</span>
        ),
      },
      {
        title: '총 환불금액',
        key: 'totalRefundAmount',
        dataIndex: 'totalRefundAmount',
        render: (item) => (
          <span style={{ color: '#FF4242' }}>-{stringToMoneyFormat(item)}</span>
        ),
      },
    ];
  }, []);

  // 주문 상세내역 데이터 정제
  const OrderColumns = React.useMemo(() => {
    return [
      {
        title: '주문ID',
        key: 'shop_order_id',
        dataIndex: 'shop_order_id',
        fixed: 'left',
        width: 100,
        sorter: (a, b) => a.shop_order_id - b.shop_order_id,
      },
      {
        title: '주문번호',
        key: 'payment_serial_number',
        dataIndex: 'payment_serial_number',
        width: 200,
        render: (item) => <p style={{ fontSize: 10 }}>{item}</p>,
      },
      {
        title: '주문회원',
        key: 'user',
        dataIndex: 'user',
        width: 200,
        render: (item) => (
          <Flex gap={4} align="flex-end">
            <p>
              {item.user_name} ({item.user_id})
            </p>
            <p style={{ fontSize: 10, color: '#BDBDBD' }}>
              ({item.user_phone})
            </p>
          </Flex>
        ),
      },
      {
        title: '상품타입',
        key: 'type',
        dataIndex: 'type',
        width: 100,
        filters: Object.entries(PRODUCT_TYPE).map(([key, value]) => ({
          text: value.title,
          value: key,
        })),
        onFilter: (value, record) => record.type === value,
        render: (item) => (
          <Tag color={PRODUCT_TYPE[item].color}>{PRODUCT_TYPE[item].title}</Tag>
        ),
      },
      {
        title: '구매건수',
        key: 'num',
        dataIndex: 'product_information',
        width: 100,
        render: (item) => stringToMoneyFormat(item.length),
        sorter: (a, b) =>
          a.product_information.length - b.product_information.length,
      },
      {
        title: '수령인',
        key: 'recipient',
        dataIndex: 'delivery_address',
        width: 100,
        render: (item, record) => item.recipient_name ?? record.user.user_name,
      },
      {
        title: '배송지/수령지',
        key: 'delivery_address',
        dataIndex: 'delivery_address',
        width: 200,
        render: (item) => (
          <Flex vertical>
            <p style={{ fontSize: 12 }}>
              ({item.recipient_postcode ?? item.forwarding_postcode}){' '}
              {item.recipient_address ?? item.forwarding_address}
            </p>
            <p style={{ fontSize: 12 }}>
              {item.recipient_address_detail ?? item.forwarding_address_detail}
            </p>
          </Flex>
        ),
      },
      {
        title: '결제금액',
        key: 'final_price',
        dataIndex: 'final_price',
        width: 130,
        render: (item) => stringToMoneyFormat(item),
        sorter: (a, b) => a.final_price - b.final_price,
      },
      {
        title: '할인금액',
        key: 'final_discount',
        dataIndex: 'final_discount',
        width: 130,
        render: (item) => stringToMoneyFormat(item),
        sorter: (a, b) => a.final_discount - b.final_discount,
      },
      {
        title: '사용포인트',
        key: 'use_point',
        dataIndex: 'use_point',
        width: 130,
        render: (item) => stringToMoneyFormat(item),
        sorter: (a, b) => a.use_point - b.use_point,
      },
      {
        title: '적립금',
        key: 'final_reward_point',
        dataIndex: 'final_reward_point',
        width: 130,
        render: (item) => stringToMoneyFormat(item),
        sorter: (a, b) => a.final_reward_point - b.final_reward_point,
      },
      {
        title: '상품금액',
        key: 'product_price',
        dataIndex: 'final_price',
        width: 130,
        render: (item, record) =>
          stringToMoneyFormat(item - record.final_delivery_price),
        sorter: (a, b) =>
          a.final_price -
          a.final_delivery_price -
          (b.final_price - b.final_delivery_price),
      },
      {
        title: '배송비',
        key: 'final_delivery_price',
        dataIndex: 'final_delivery_price',
        width: 130,
        render: (item) => stringToMoneyFormat(item),
        sorter: (a, b) => a.final_delivery_price - b.final_delivery_price,
      },
      {
        title: '결제상태',
        key: 'payment_state',
        dataIndex: 'payment_state',
        width: 100,
        filters: Object.entries(PAYMENT_STATE).map(([key, value]) => ({
          text: value.title,
          value: key,
        })),
        onFilter: (value, record) => {
          const {
            parcel_service = [],
            rental = [],
            product_information,
          } = record;
          const refundLength = [...parcel_service, ...rental].reduce(
            (acc, cur) => {
              const { refund } = cur;

              if (refund) acc += 1;

              return acc;
            },
            0
          );

          const paymentState = refundLength
            ? refundLength === product_information.length
              ? 'REFUNDED'
              : 'PARTIALLY_REFUNDED'
            : 'COMPLETED';

          return paymentState === value;
        },
        render: (item, record) => {
          const {
            parcel_service = [],
            rental = [],
            product_information,
          } = record;
          const refundLength = [...parcel_service, ...rental].reduce(
            (acc, cur) => {
              const { refund } = cur;

              if (refund) acc += 1;

              return acc;
            },
            0
          );

          const paymentState = refundLength
            ? refundLength === product_information.length
              ? 'REFUNDED'
              : 'PARTIALLY_REFUNDED'
            : 'COMPLETED';

          return (
            <Tag color={PAYMENT_STATE[paymentState].color}>
              {PAYMENT_STATE[paymentState].title}
            </Tag>
          );
        },
      },
      {
        title: '주문일시',
        key: 'created_at',
        dataIndex: 'created_at',
        render: (item) => (
          <p style={{ fontSize: 12 }}>
            {dayjs.unix(item).locale('ko').format('YYYY-MM-DD (dd) HH:mm:ss')}
          </p>
        ),
        sorter: (a, b) => a.created_at - b.created_at,
      },
    ];
  }, [PAYMENT_STATE]);
  /**!SECTION - Hooks */

  /**
   * SECTION - Render
   */
  return (
    <ModalLayout
      title={`${date} 주문 상세내역`}
      type="drawer"
      open={open}
      onCancel={onClose}
      closable
      maxWidth={1500}
      width={'100%'}
    >
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={0}>
          <Row gutter={[16, 16]}>
            <Col x={24}>
              <Table
                columns={SummaryColumns}
                dataSource={[summary]}
                pagination={false}
                bordered
                locale={{
                  emptyText: '판매 데이터가 없습니다',
                }}
                scroll={{ x: 150 * SummaryColumns.length }}
              />
            </Col>

            <Col x={24}>
              <Table
                columns={OrderColumns}
                dataSource={Orders}
                pagination={false}
                bordered
                locale={{
                  emptyText: '판매 데이터가 없습니다',
                }}
                scroll={{ x: 150 * OrderColumns.length }}
                expandable={{
                  expandedRowRender: (currentRow) => (
                    <Table
                      columns={getChildrenColumns(currentRow.type)}
                      dataSource={getChildrenData(currentRow)}
                      pagination={false}
                      scroll={{
                        x: 200 * getChildrenColumns(currentRow.type).length,
                      }}
                      bordered
                    />
                  ),
                }}
              />
            </Col>
          </Row>
        </Content>
      </Spin>
    </ModalLayout>
  );
  /**!SECTION - Render */
};

export default ShopSalesDetail;
